import { Empty, Space, Spin } from 'antd';
import React, { CSSProperties, FC, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getProductId } from '@/infrastructure/product-info';
import { decimal2Percentile } from '@/infrastructure/utils/to-unit';
import { getTopData } from '@/presentation/components/trend/api';
import { IGetTopDataReq, MetricSubType, MetricType } from '@/proto/metric';
import { DetailView } from './detail-view';
import styles from './index.module.scss';
import { ProgressItem } from './progress-item';

type PropsWithStyles<T> = T & {
  style?: CSSProperties;
  className?: string;
};

export interface ItemProps {
  data?: any;
  strokeColor?: string;
  metricName: string;
  dimensionName: string;
  token: string;
  metricSubType: MetricSubType;
  selectMetricType: MetricSubType;
  timeWindow: number;
}
export interface IItemProgressData{
  valueRatio: number;
  value: number;
  field: string;
}
export interface IDistributionData {
  distributions: IItemProgressData[],
}

export const Item: FC<PropsWithStyles<ItemProps>> = (props) => {
  const { t } = useTranslation();
  const { token, metricSubType, dimensionName, selectMetricType, timeWindow } = props;
  const itemRef = useRef<HTMLDivElement>(null);
  const [data, setData] = useState<IDistributionData>({
    distributions: [],
  });
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    const params: IGetTopDataReq = {
      productId: getProductId(),
      token,
      limit: 5,
      timeWindow,
      type: MetricType.TYPE_TRAFFIC_RESOURCE,
      subTypes: [metricSubType],
      dimensionNames: [dimensionName],
      extendOption: { sortField: selectMetricType, sortType: 'DESC' },
    };

    const res = await getTopData(params);
    // 过滤field为空的数据
    const filterDistributions = res.records.filter(item => item.dimensionValue).map<IItemProgressData>((item) => {
      const total = res.total || 0;
      let metricValue = 0;
      let metricCount = 0;

      if (selectMetricType === MetricSubType.SUB_TYPE_COUNT) {
        metricValue = item.metricValues?.[1] ?? 0;
        metricCount = item.metricValues?.[0] ?? 0;
      } else if (selectMetricType === MetricSubType.SUB_TYPE_DEVICE_UV) {
        metricValue = item.metricValues?.[5] ?? 0;
        metricCount = item.metricValues?.[4] ?? 0;
      } else if (selectMetricType === MetricSubType.SUB_TYPE_USER_UV) {
        metricValue = item.metricValues?.[3] ?? 0;
        metricCount = item.metricValues?.[2] ?? 0;
      }

      return {
        value: metricValue,
        valueRatio: metricCount ? metricCount / total : 0,
        field: item.dimensionValue || '',
      };
    });

    setData({
      distributions: filterDistributions,
    });
    setLoading(false);
  };

  const maxLabelWidth = useMemo(
    () => (itemRef.current ? itemRef.current.clientWidth * 0.7 : undefined),
    [itemRef.current],
  );

  useEffect(() => {
    if (token) {
      load();
    }
  }, [token, selectMetricType, metricSubType]);

  return (
    <>
      <div className={styles.rankItem} ref={itemRef}>
        <div className={styles.rankItemHeader}>
          <span className={styles.title}>{props.metricName} Top5</span>
          {data?.distributions?.length ? <DetailView
              timeWindow={timeWindow}
            selectMetricType={selectMetricType}
            metricSubType={metricSubType}
            metricName={props.metricName}
            token={token}
            dimensionName={dimensionName}
          /> : null}
        </div>
        <Spin spinning={loading}>
          <div className={styles.content}>
            {!data?.distributions?.length ? <Empty /> : <table className={styles.cardTable} style={{ width: '100%', tableLayout: 'auto' }}>
              {data.distributions.map((item, index) => {
                if (index > 4) return null;
                return <tr>
                <td><ProgressItem
                  labelWidth={maxLabelWidth}
                  record={item}
                  strokeColor={props.strokeColor}
                /></td>
                <td className={styles.value}>
                  <Space size={0}>
                    <span>{decimal2Percentile(item.value, 2, false, false)}</span>
                    <span className={styles.smallCharacters}>%</span>
                  </Space>
                  {index === 0 ? <div className={styles.valueTitle}>{t('异常率')}</div> : null}
                </td>
                <td className={styles.ratio}>
                  <Space size={0}>
                    <span>{decimal2Percentile(item.valueRatio, 2, false, false)}</span>
                    <span className={styles.smallCharacters}>%</span>
                  </Space>
                  {index === 0 ? <div className={styles.valueTitle}>{t('异常占比')}</div> : null}
                </td>
              </tr>;
              })}
            </table>}
          </div>
        </Spin>
      </div>
    </>
  );
};
